import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignaturePad from '../components/SignaturePad';
import '../styles/HptScreens.css';

// BackEnd de Serveless
const API_URL = process.env.REACT_APP_API_URL;

// ============================================================================
// COMPONENTE: ExpandableCard (Acordeón reutilizable)
// ============================================================================
const ExpandableCard = ({ title, icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="expandable-card">
            <div className="card-header" onClick={() => setIsOpen(!isOpen)}>
                <span>{icon} {title}</span>
                <span>{isOpen ? '▲' : '▼'}</span>
            </div>
            {/* Si isOpen es true, le añade la clase 'open' para mostrar el contenido */}
            <div className={`card-content ${isOpen ? 'open' : ''}`}>
                {children}
            </div>
        </div>
    );
};

// ============================================================================
// COMPONENTE: Gestor de Descargos (Bloqueos LOTO)
// ============================================================================
const DescargoManager = ({ label, count, setCount, refs, setRefs }) => {
    
    // Función que se ejecuta cuando el usuario escribe en uno de los inputs dinámicos
    const handleChange = (idx, val) => {
        const newRefs = [...refs]; // Copiamos el array actual
        newRefs[idx] = val;        // Actualizamos posicion del array
        setRefs(newRefs);          // Guardamos el nuevo array
    };

    return (
        <div className="descargo-col">
            <label className="mini-label">{label}:</label>
            {/* Input numérico para decir CUÁNTOS descargos hay */}
            <input 
                type="number" 
                min="0" 
                value={count} 
                onChange={e => setCount(parseInt(e.target.value) || 0)} 
                className="num-input" 
            />
            {/* Bucle que genera tantos inputs de texto como el número que hayamos puesto arriba */}
            {[...Array(count)].map((_, i) => (
                <input 
                    key={i} 
                    placeholder="Ref..." 
                    className="ref-input" 
                    value={refs[i] || ''} 
                    onChange={(e) => handleChange(i, e.target.value)} 
                />
            ))}
        </div>
    );
};

// ============================================================================
// COMPONENTE PRINCIPAL: CreatePTWDirectoScreen
// ============================================================================
const CreatePTWDirectoScreen = () => {
    const navigate = useNavigate();
    
    // --- ESTADOS DE DATOS (Servidor) ---
    const [activos, setActivos] = useState([]);       // Lista de activos
    const [catalogo, setCatalogo] = useState([]);     // Catálogo general de riesgos y medidas de seguridad
    const [seleccionados, setSeleccionados] = useState([]); // Array con los IDs de los riesgos marcados
    const [showSignature, setShowSignature] = useState(false); // Modal de Firmas
    
    // --- ESTADOS DEL FORMULARIO PTW ---
    const [alcance, setAlcance] = useState('');
    // Pillamos el nombre del usuario directamente de la sesión (Local Storage)
    const [idActivo, setIdActivo] = useState(null);
    const [solicitanteNombre, setSolicitanteNombre] = useState(localStorage.getItem('user_name') || '');
    const [denActSnapshot, setDenActSnapshot] = useState(''); // Se guarda como texto por si el activo cambia de nombre
    const [otAsociada, setOtAsociada] = useState('');
    const [fechaTrabajo, setFechaTrabajo] = useState(new Date().toISOString().split('T')[0]); // Fecha de hoy por defecto
    const [rangoHorario, setRangoHorario] = useState('');

    // --- ESTADOS DEL BUSCADOR AUTOCOMPLETAR (ACTIVOS) ---
    const [busquedaActivo, setBusquedaActivo] = useState('');     // Texto a Escribir por el usuario
    const [mostrarDesplegable, setMostrarDesplegable] = useState(false); // MUestra resultados en columna
    const [opcionesActivos, setOpcionesActivos] = useState([]);   // Resultados de la búsqueda desde el servidor

    // --- ESTADOS PARA LOS DESCARGOS (BLOQUEOS DE OPERACIÓN) ---
    // Cada tipo de descargo necesita 2 estados: Numero que hay y un Array con las referencias
    const [nElecOp, setNElecOp] = useState(0); 
    const [refsElecOp, setRefsElecOp] = useState([]);
    
    const [nDescElecOp, setNDescElecOp] = useState(0); 
    const [refsDescElecOp, setRefsDescElecOp] = useState([]);
    
    const [nDescMecOp, setNDescMecOp] = useState(0); 
    const [refsDescMecOp, setRefsDescMecOp] = useState([]);
    
    // --- ESTADOS PARA LOS DESCARGOS (MANTENIMIENTO) ---
    const [nElecMan, setNElecMan] = useState(0); 
    const [refsElecMan, setRefsElecMan] = useState([]);
    
    const [nDescElecMan, setNDescElecMan] = useState(0); 
    const [refsDescElecMan, setRefsDescElecMan] = useState([]);
    
    const [nDescMecMan, setNDescMecMan] = useState(0); 
    const [refsDescMecMan, setRefsDescMecMan] = useState([]);

    // ============================================================================
    // Efectos al montar Componente.
    // ============================================================================
    useEffect(() => {
        const token = localStorage.getItem('userToken');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        const fetchData = async () => { 
            try {
                // Descargamos Activos y Catálogo de Seguridad a la vez
                const [resActivos, resCatalogo] = await Promise.all([
                    axios.get(`${API_URL}/api/catalogos/activos`, config),
                    axios.get(`${API_URL}/api/catalogos/seguridad`, config)
                ]);
                const extractData = (res) => res.data.results ? res.data.results : res.data;
                setActivos(extractData(resActivos));
                setCatalogo(extractData(resCatalogo));
            } catch (err) { console.error(err); }
        };
        fetchData();
    }, []);

    // ============================================================================
    // FUNCIONES AUXILIARES
    // ============================================================================
    // Alterna el estado de los checkbox
    const toggleItem = (idItem) => {
        setSeleccionados(prev => prev.includes(idItem) 
            ? prev.filter(i => i !== idItem) // Si ya estaba, lo quitamos
            : [...prev, idItem]              // Si no estaba, lo metemos
        );
    };

    // ============================================================================
    // FUNCIONES: BUSCADOR DE ACTIVOS (AUTOCOMPLETAR)
    // ============================================================================
    // Se ejecuta al escribir en el campo "codigo activo"
    const buscarActivosEnServidor = async (texto) => {
        setBusquedaActivo(texto);
        
        // Al menos tres letras, para realizar peticiones al servidor
        if (texto.length < 2) {
            setOpcionesActivos([]);
            return;
        }

        try {
            const token = localStorage.getItem('userToken');
            const res = await axios.get(`${API_URL}/api/catalogos/activos/buscar?q=${texto}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Guarda los resultados en el estado para mostrarlos en la lista desplegable
            setOpcionesActivos(res.data.results || []);
        } catch (err) {
            console.error("Error buscando activos:", err);
        }
    };

    // Al hacer clic en una opción de la lista desplegable
    const seleccionarActivo = (activo) => {
        setIdActivo(activo.id_activo);
        setDenActSnapshot(activo.denominacion);
        setBusquedaActivo(activo.codigo_activo); // Deja el código en el input
        setMostrarDesplegable(false); // Cierra la lista
    };

    // ============================================================================
    // GUARDADO (Envío al Backend)
    // ============================================================================
    const handleGuardarNuevo = async () => {
        const token = localStorage.getItem('userToken');
        const currentUserId = localStorage.getItem('user_id'); // ID del creador

        

        // 1. Construcción del Payload para mandar al Backend.
        const payload = {
            id_activo: parseInt(idActivo),
            id_solicitante: parseInt(currentUserId), 
            den_act_snapshot: denActSnapshot,
            solicitante_nombre: solicitanteNombre,
            ot_asociada: otAsociada || null,
            fecha_trabajo: fechaTrabajo, 
            rango_horario: rangoHorario || null,
            estado: 'creado', // Estado inicial del permiso
            
            // Aseguramos que los IDs de seguridad sean INT para la base de datos
            seleccionados_seguridad: seleccionados.map(id => parseInt(id)),

            // Empaquetamos en JSON
            descargos: {
                operacion: { nElecOp, nDescElecOp, nDescMecOp, refsElecOp, refsDescElecOp, refsDescMecOp },
                mantenimiento: { nElecMan, nDescElecMan, nDescMecMan, refsElecMan, refsDescElecMan, refsDescMecMan }
            },
            
            // Campos vacíos por defecto obligatorios para la estructura del backend
            firmas_secciones: {},
            renovaciones: []
        };

        console.log("Enviando Payload:", payload); 

        // 2. Envío al servidor
        try {
            await axios.post(`${API_URL}/api/ptw`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("✅ Permiso Directo creado con éxito.");
            navigate('/dashboard'); // Si todo correcto, volvemos a Dashboard
        } catch (err) {
            // Si da algun fallo mostraemos error
            const errores = err.response?.data?.detail;
            console.error("Detalle del error:", errores);
            
            // Si BackEND (422), Falta algun dato
            if (err.response?.status === 422) {
                alert(`❌ Error 422 de validación. Falta algún campo por rellenar.`);
            } else {
                alert("❌ Error al conectar con el servidor.");
            }
        }
    };

    // Extraemos solo las categorías de los elementos marcados como "Riesgo" en el catálogo
    const categoriasRiesgo = [...new Set(catalogo.filter(i => i.es_riesgo).map(i => i.categoria))];
    
    // ============================================================================
    // RENDER (HTML/JSX)
    // ============================================================================
    return (
        <div className="container">
            {/* --- CABECERA --- */}
            <div className="top-header header-ptw">
                <div className="d-flex align-center">
                    <span className="ptw-label">NUEVO PERMISO:</span>
                    <div className="ptw-badge">
                        <span style={{ fontSize: '20px' }}>⚡</span> DIRECTO
                    </div>
                </div>
                <div className="header-actions">
                    <button onClick={handleGuardarNuevo} className="btn-save">💾 Crear y Guardar</button>
                    <button onClick={() => navigate('/dashboard')} className="btn-close">Cancelar</button>
                </div>
            </div>

            {/* --- SECCIÓN PRINCIPAL: DATOS BÁSICOS --- */}
            <div className="main-card">
                <div className="row">
                    <div className="d-flex" style={{ flex: 2, flexDirection: 'column' }}>
                        <label className="info-label">ALCANCE DEL TRABAJO:</label>
                        <textarea 
                            placeholder="Describe el trabajo..." 
                            value={alcance} 
                            onChange={e => setAlcance(e.target.value)} 
                            className="main-textarea" 
                        />
                    </div>
                    <div className="field">
                        <label className="info-label">SOLICITANTE:</label>
                        <input 
                            value={solicitanteNombre} 
                            onChange={e => setSolicitanteNombre(e.target.value)} 
                            className="input-solicitante" 
                        />
                    </div>
                </div>
                
                <div className="row">
                    {/* BUSCADOR DE ACTIVOS (AUTOCOMPLETAR) */}
                    <div className="info-box flex-1 relative">
                        <span className="info-label">CÓDIGO ACTIVO (Buscador):</span>
                        
                        <input 
                            type="text" 
                            className="main-input" 
                            placeholder="Escriba código o nombre..."
                            value={busquedaActivo}
                            onChange={(e) => {
                                buscarActivosEnServidor(e.target.value);
                                setMostrarDesplegable(true);
                                // Si borra el texto, reseteamos el activo seleccionado
                                if (idActivo) {
                                    setIdActivo(null);
                                    setDenActSnapshot('');
                                }
                            }}
                            onFocus={() => setMostrarDesplegable(true)}
                            // Se usa un timeout para que de tiempo a pulsar la opción antes de que desaparezca
                            onBlur={() => setTimeout(() => setMostrarDesplegable(false), 200)}
                        />
                        {/* Lista flotante de resultados del buscador */}
                        {mostrarDesplegable && busquedaActivo.length >= 2 && (
                            <ul className="autocomplete-list">
                                {opcionesActivos.length > 0 ? (
                                    opcionesActivos.map(a => (
                                        <li 
                                            key={a.id_activo} 
                                            className="autocomplete-item"
                                            onMouseDown={() => seleccionarActivo(a)} 
                                        >
                                            <strong>{a.codigo_activo}</strong> <br/>
                                            <span className="text-small text-muted">{a.denominacion}</span>
                                        </li>
                                    ))
                                ) : (
                                    <li className="autocomplete-item">No se encontraron activos...</li>
                                )}
                            </ul>
                        )}
                    </div>
                    <div className="field">
                        <label className="info-label">DENOMINACIÓN:</label>
                        {/* Campo bloqueado (readOnly) porque se autocompleta con el Select anterior */}
                        <input value={denActSnapshot} readOnly className="read-only" />
                    </div>
                    <div className="field">
                        <label className="info-label">OT (Opcional):</label>
                        <input value={otAsociada} onChange={e => setOtAsociada(e.target.value)} className="main-input" />
                    </div>
                </div>

                <div className="row">
                    <div className="field">
                        <label className="info-label">FECHA:</label>
                        <input type="date" value={fechaTrabajo} onChange={e => setFechaTrabajo(e.target.value)} className="main-input" />
                    </div>
                    <div className="field">
                        <label className="info-label">HORARIO:</label>
                        <input placeholder="08:00 - 16:00" value={rangoHorario} onChange={e => setRangoHorario(e.target.value)} className="main-input" />
                    </div>
                </div>
            </div>

            {/* --- SECCIÓN: DESCARGOS Y BLOQUEOS LOTO --- */}
            <ExpandableCard title="Descargos Requeridos (Operación)" icon="⚙️">
                <div className="descargos-grid">
                    <DescargoManager label="Bloqueos Eléc." count={nElecOp} setCount={setNElecOp} refs={refsElecOp} setRefs={setRefsElecOp} />
                    <DescargoManager label="Descargos Eléc." count={nDescElecOp} setCount={setNDescElecOp} refs={refsDescElecOp} setRefs={setRefsDescElecOp} />
                    <DescargoManager label="Descargos Mec." count={nDescMecOp} setCount={setNDescMecOp} refs={refsDescMecOp} setRefs={setRefsDescMecOp} />
                </div>
            </ExpandableCard>

            {/* --- SECCIÓN: RIESGOS Y MEDIDAS --- */}
            <ExpandableCard title="Selección de Riesgos y Medidas" icon="🚨">
                <div className="category-grid">
                    {categoriasRiesgo.map(cat => (
                        <div key={cat} className="cat-block">
                            <div className="cat-title">{cat}</div>
                            <div className="grid-checks-auto">
                                {/* Filtramos el catálogo global para mostrar solo los de esta categoría */}
                                {catalogo.filter(i => i.categoria === cat).map(item => {
                                    const isSelected = seleccionados.includes(item.id_item);
                                    
                                    // Pintamos de rojo si es riesgo o de verde si es medida
                                    let badgeClass = 'check-item';
                                    if (isSelected) {
                                        badgeClass += item.es_riesgo ? ' checked-risk' : ' checked-measure';
                                    }

                                    return (
                                        <label key={item.id_item} className={badgeClass}>
                                            <input type="checkbox" checked={isSelected} onChange={() => toggleItem(item.id_item)} />
                                            <span><strong>{item.codigo}</strong> - {item.descripcion}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </ExpandableCard>
        </div>
    );
};

export default CreatePTWDirectoScreen;